import {Inject, Injectable} from '@angular/core';
import {API_BASE_URL_BACKEND, DcBaseInitializationService, DcCoreBackendBasePath,} from '@datachain/ui-sdk/common';
import {OrderedMap, Set} from 'immutable';

import {DxListsStorage} from '../core/dx-lists.storage';
import {DcDxGrids} from '../core/dx-types';
import {ProjectStorage} from '../core/project.storage';
import {TagAdapter} from '../core/tag.adapter';
import {IEntityPersistenceStateResponse, IEntityResponse, IPersistenceState} from '../requesters/entity/entity.model';
import {EntityItemEntity, PersistenceState} from './components/entity-list/entity-item.entity';
import {EntityListEntity, IEntityListExtra,} from './components/entity-list/entity-list.entity';

@Injectable()
export class EntityAdapter {
	public constructor(
		private readonly tagAdapter: TagAdapter,
		@Inject(API_BASE_URL_BACKEND)
		private readonly apiBase: DcCoreBackendBasePath,
		private readonly dxListsStorage: DxListsStorage,
		private readonly userSettings: ProjectStorage
	) {}

	public generateEntityList(
		response: ReadonlyArray<IEntityResponse>
	): EntityListEntity {
		const list = this.generateEntityListStructure();

		const items = response
			.reduce<Array<EntityItemEntity>>((acc, item) => {
				const temp = this.generateEntityItemEntity(item);
				acc.push(temp);
				return acc;
			}, [])
			.sort((a, b) => {
				if (!a.updateDate) {
					throw new Error('Date is null!');
				}
				if (!b.updateDate) {
					throw new Error('Date is null!');
				}

				return b.updateDate.getTime() - a.updateDate.getTime();
			});

		const orderedMap = OrderedMap<string, EntityItemEntity>(
			items.map((item) => [`${item.id}`, item])
		);
		return list.setElements(orderedMap);
	}

	public updateListWithEntitiesFrameStatus(
		list: EntityListEntity,
		result: ReadonlyArray<IEntityPersistenceStateResponse>
	): EntityListEntity {
		return list.updatePersistenceState(result.map((it) => [
			it.id,
			this.generateEntityItemPersistenceState(it.status)
		]));
	}

	public generateEntityListStructure(): EntityListEntity {
		let availableCols: Array<IEntityListExtra> = [
			{
				field: 'isActive',
				alias: $localize`:i18n=@@general.status:`,
				type: 'boolean',
				isSearchable: false,
				isFilterable: false,
				hasHeaderFilter: true,
				visibleIndex: 0,
			},
			{
				field: 'code',
				alias: $localize`:i18n=@@entity.list.column.code:`,
				type: 'string',
				isSearchable: true,
				isFilterable: true,
				hasHeaderFilter: false,
				visible: false,
				visibleIndex: 1,
			},
			{
				field: 'label',
				alias: $localize`:i18n=@@entity.list.column.label:`,
				type: 'string',
				isSearchable: true,
				isFilterable: true,
				hasHeaderFilter: false,
				visibleIndex: 2,
			},
			{
				field: 'parentProject',
				alias: $localize`:i18n=@@entity.list.column.project:`,
				type: 'string',
				isSearchable: true,
				isFilterable: true,
				hasHeaderFilter: true,
				visible: false,
				visibleIndex: 3,
			},
			{
				field: 'tags',
				alias: $localize`:i18n=@@entity.list.column.tags:`,
				type: '',
				isSearchable: false,
				hasHeaderFilter: true,
				visibleIndex: 4,
			},
			{
				field: 'description',
				alias: $localize`:i18n=@@entity.list.column.description:`,
				type: 'string',
				isSearchable: true,
				isFilterable: true,
				hasHeaderFilter: false,
				visible: false,
				visibleIndex: 5,
			},
			{
				field: 'isImported',
				alias: $localize`:i18n=@@entity.list.column.imported:`,
				type: 'boolean',
				isSearchable: false,
				isFilterable: false,
				hasHeaderFilter: true,
				visibleIndex: 6,
				visible: false,
			},
			{
				field: 'persistenceState',
				alias: $localize`:i18n=@@entity.list.column.persistence:`,
				type: 'boolean',
				isSearchable: false,
				isFilterable: false,
				hasHeaderFilter: true,
				visibleIndex: 7,
			},
			{
				field: 'creationDate',
				alias: $localize`:i18n=@@entity.list.column.creationDate:`,
				type: 'datetime',
				format: 'yyyy/MM/dd',
				isSearchable: true,
				isFilterable: true,
				hasHeaderFilter: false,
				visible: false,
				visibleIndex: 8,
			},
			{
				field: 'createdByAvatar',
				alias: $localize`:i18n=@@entity.list.column.createdBy:`,
				type: 'string',
				isSearchable: false,
				isFilterable: true,
				hasHeaderFilter: true,
				visible: false,
				visibleIndex: 9,
			},
			{
				field: 'updateDate',
				alias: $localize`:i18n=@@entity.list.column.updateDate:`,
				type: 'datetime',
				format: 'yyyy/MM/dd',
				isSearchable: true,
				isFilterable: true,
				hasHeaderFilter: false,
				visibleIndex: 10,
			},
			{
				field: 'updatedByAvatar',
				alias: $localize`:i18n=@@entity.list.column.updatedBy:`,
				type: 'string',
				isSearchable: false,
				isFilterable: true,
				hasHeaderFilter: true,
				visibleIndex: 11,
			},
		];

		availableCols = this.dxListsStorage.synchronize<
			IEntityListExtra,
			EntityItemEntity
		>(DcDxGrids.EntityListStandalone, availableCols);
		return EntityListEntity.build(availableCols);
	}

	private generateEntityItemEntity(
		response: IEntityResponse
	): EntityItemEntity {
		const resourceUrl =
			DcBaseInitializationService.getResource('PICTOGRAMME_IMAGE');
		const elemProjectId = response.project?.id ?? -1;
		const belongsToProject = this.userSettings.getProjectId() === elemProjectId;
		return EntityItemEntity.build({
			id: response.id,
			label: response.label ?? '',
			code: response.code ?? '',
			description: response.description ?? '',
			isImported: response.imported,
			parentProject: {
				pictoUrl: `${this.apiBase.getBasePath()}/${resourceUrl}?id=${
					response.project?.icon_id ?? -1
				}`,
				label: response.project?.label ?? '',
				isInSameProject: belongsToProject,
				isPublic: response.project?.is_public ?? false,
				charCount: 0,
				depotCount: 0,
			},
			persistenceState: {
				recentJobId: response.status?.job_id ?? null,
				isPersisted: response.status?.persisted ?? false,
				date: response.status?.persistence_date
					? new Date(response.status?.persistence_date)
					: null,
				count: response.status?.current_repartition_number ?? 0,
				duration: response.status?.persistence_execution_time ?? 0,
				size: response.status?.persistence_size ?? 0,
				isDefault: true,
			},
			updateDate:
				response.update_date === undefined
					? new Date(response.creation_date ?? '')
					: new Date(response.update_date),
			creationDate: response.creation_date
				? new Date(response.creation_date)
				: null,
			updatedBy:
				response.updated_by === undefined
					? response.created_by
						? `${response.created_by.first_name} ${response.created_by.last_name}`
						: ''
					: `${response.updated_by.first_name} ${response.updated_by.last_name}`,
			createdBy: response.created_by
				? `${response.created_by.first_name} ${response.created_by.last_name}`
				: '',
			tags: this.tagAdapter.generateResponseMetadataTag(response.tags ?? []),
			isActive: response.active ?? false,
			pictogramUrl: `${this.apiBase.getBasePath()}/${resourceUrl}?id=${
				response.icon_id ?? -1
			}`,
			isActionInProgress: response.status?.job_status === 'RUNNING',
		});
	}

	private generateEntityItemPersistenceState(status: Partial<IPersistenceState>): PersistenceState {
		return {
			recentJobId: status?.job_id ?? null,
			isPersisted: status?.persisted ?? false,
			date: status?.persistence_date
				? new Date(status?.persistence_date)
				: null,
			count: status?.current_repartition_number ?? 0,
			duration: status?.persistence_execution_time ?? 0,
			size: status?.persistence_size ?? 0,
			isDefault: false
		};
	}

	private synchronizeWithCurrentPersistenceState(
		item: EntityItemEntity,
		onGoingPersistence: Set<number>
	): EntityItemEntity {
		if (onGoingPersistence.has(item.id)) {
			return item.setOnGoingPersistence();
		}
		return item;
	}
}
